import type { Glyph } from '../../flair/engine';
import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { type Ellipse, type HeadPicture, inEllipse, mask, type SetFlair } from './mythic';

/**
 * The Magician set (ADR-0160 amended, mythic): a stage conjurer. A tall glossy top hat with a rabbit in it, a domino
 * mask and a waxed moustache, a tailcoat and a cape, and Abracadabra, a wand that fires playing cards.
 *
 * The head is a mask over the head that leaves the face bare (the gardener's own eyes look out of the mask's holes): a
 * tall black silk top hat, lit down its near side in a gloss streak, its band in the team's colour, its brim curled up at
 * the sides; black hair slicked back under it; a domino mask in the team's colour over the eyes; a curled,
 * waxed black moustache over the mouth, its tips curling up past the cheeks. Lying, the eyes in the mask are crosses.
 * Its flair: a white rabbit that pops up out of the hat now and then (from behind its crown), wiggles its ears, blinks
 * and ducks back down (the bright moving part at 1:1), and a sparkle twinkling on the hat's gloss.
 * The suit is a black tailcoat, satin lapels open on a waistcoat in the team's colour with gold buttons, a white shirt
 * front with a wing collar and a black bow tie, a white pocket square, a white-gloved hand on the near arm; black shoes.
 * Its flair: a black cape lined in the team's colour swaying behind, the coat's two tails swinging under it, sparkles
 * twinkling off the white glove.
 */

// --- The top hat, the mask and the moustache --------------------------------------------------------------------------

/** The head under the hat (u forward, v down from the head's middle): where its hair is. */
const SKULL: Ellipse = [0, -1, 19.5, 18.5];
/** The hat: its crown's sides at the brim and at the top (they flare out a little), its top and the brim's line. */
const CROWN = { u0: -12.5, u1: 11.5, flare: 1.2, top: -26.5, brim: -8 } as const;
/** The brim: a flat ellipse round the crown's foot. */
const BRIM: Ellipse = [-0.5, -7.2, 19.5, 3];
/** The domino mask's holes [u, v, rx, ry], on the look's round eyes. */
const HOLES = [
  [0.1, 8.6, 2.6, 3.6],
  [11, 8.6, 2.3, 3.4],
] as const;

/** The crown's half-width round its middle at row `v` (flaring toward its top). */
const crownAt = (v: number) => {
  const t = (CROWN.brim - v) / (CROWN.brim - CROWN.top);
  return [CROWN.u0 - CROWN.flare * t, CROWN.u1 + CROWN.flare * t] as const;
};

function hat(u: number, v: number): PaletteKey | null {
  // The top: a flat ellipse, its near rim lit.
  const [a, b] = crownAt(CROWN.top);
  const lid: Ellipse = [(a + b) / 2, CROWN.top + 0.6, (b - a) / 2, 2.2];
  const top = inEllipse(u, v, lid);
  if (top <= 1) {
    if (top > 0.6) return v < lid[1] ? '3' : u < lid[0] ? '2' : '1';
    return u < lid[0] - 4 ? '2' : '1';
  }
  // The crown: silk, a gloss streak down its near third, its far side in shadow; the band in the team's colour.
  const [l, r] = crownAt(v);
  if (v > CROWN.top && v <= CROWN.brim && u >= l && u <= r) {
    const t = (u - l) / (r - l);
    if (v > CROWN.brim - 4.6) {
      if (v < CROWN.brim - 3.8) return t < 0.3 ? 'c' : 'b';
      if (t > 0.78) return 'B';
      return t < 0.22 ? 'c' : 'b';
    }
    if (t < 0.06) return '3';
    if (t > 0.92) return 'k';
    if (t > 0.17 && t < 0.25) return v < CROWN.brim - 9 ? '5' : '4';
    if (t > 0.12 && t < 0.3) return '3';
    return t < 0.55 ? '2' : t < 0.8 ? '1' : 'k';
  }
  // The brim: its top lit at the front, curled up at either side.
  const brim = inEllipse(u, v, BRIM);
  if (brim <= 1) {
    if (v < BRIM[1] - 1.2 && brim > 0.55) return u < BRIM[0] ? '3' : '2';
    if (v > BRIM[1] + 1.3) return 'k';
    return brim > 0.8 && Math.abs(u - BRIM[0]) > 17 ? '3' : u < 0 ? '2' : '1';
  }
  return null;
}

/** The waxed moustache under the mask: two thick curls out of the middle, their tips turning up and round. */
function moustache(u: number, v: number): PaletteKey | null {
  const mid = 5.5;
  const du = u - mid;
  const side = du < 0 ? -1 : 1;
  const d = Math.abs(du);
  // Each half: thick at the middle, thinning as it sweeps out and up; its far one reaches past the face.
  const reach = side > 0 ? 12.5 : 12;
  if (d < reach) {
    const t = d / reach;
    const cy = 12.4 - t * t * 3.2;
    const half = 2.2 * (1 - t * 0.55);
    if (Math.abs(v - cy) <= half) return v < cy - half * 0.4 ? '3' : v > cy + half * 0.5 ? 'k' : '1';
  }
  // The curl at each tip: a little hook turned up and back in.
  const [cu, cv] = [mid + side * (reach + 0.3), 7.6];
  const ring = Math.hypot(u - cu, v - cv);
  if (ring < 2.1 && ring > 0.9 && (u - cu) * side > -1) return v < cv ? '2' : '1';
  return null;
}

/**
 * The domino mask in the team's colour: a band over both eyes, swept up to a point at its back, its holes open on the
 * look's eyes (crosses on the dead).
 */
function domino(u: number, v: number, lying: boolean): PaletteKey | null {
  // The holes.
  for (const [hu, hv, rx, ry] of HOLES) {
    const h = inEllipse(u, v, [hu, hv, rx, ry]);
    if (h > 1) continue;
    if (!lying) return null;
    return Math.abs(Math.abs(u - hu) - Math.abs(v - hv) * 0.8) < 0.7 ? 'w' : 'k';
  }
  if (u < -8.5 || u > 16.8) return null;
  // Its top: level, swept up into a point at the back; its bottom: round under each eye, notched over the nose.
  const top = u < -4 ? 4.3 - (-4 - u) * 0.75 : u > 4.6 && u < 6.6 ? 5.1 : 4.3;
  const bottom = u < 5.6 ? 12.3 - (u / 7.5) ** 2 * 3 : 11.9 - ((u - 11) / 5.8) ** 2 * 3;
  if (v < top || v > bottom) return null;
  // A dark lip round its holes, its top edge lit, its lower edge shaded.
  for (const [hu, hv, rx, ry] of HOLES) if (inEllipse(u, v, [hu, hv, rx + 0.9, ry + 0.9]) <= 1) return 'B';
  if (v < top + 1) return 'c';
  if (v > bottom - 1 || u > 15.8) return 'B';
  return v < 6.5 ? 'c' : 'b';
}

/** The hair slicked back under the hat: black, combed in lit lines; the ear and the face left bare. */
function hair(u: number, v: number): PaletteKey | null {
  if (inEllipse(u, v, SKULL) > 1 || v < CROWN.brim) return null;
  const back = u < -15 ? v < 11 : u < -4 ? v < 3 - (u + 4) * 0.25 : v < -5.5;
  if (!back) return null;
  if (inEllipse(u, v, SKULL) > 0.82) return 'k';
  // Combed back: a few lit lines through it.
  return Math.round(v * 0.6 + u * 0.3) % 4 === 0 && u > -16 ? '2' : '1';
}

const head: HeadPicture = (u, v, _lx, _ly, lying) => hat(u, v) ?? moustache(u, v) ?? domino(u, v, lying) ?? hair(u, v);

// --- The tailcoat ----------------------------------------------------------------------------------------------------

/**
 * The tailcoat standing (the frame's x 5-56, y 39-57): black, satin lapels open on a white shirt front, a bow tie and a
 * waistcoat in the team's colour with gold buttons; a pocket square; a white glove on the near arm.
 */
function tailcoat(): Part {
  const [x0, y0] = [5, 39];
  const c = new PixelCanvas(52, 19);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  const inSuit = (x: number, y: number) => {
    if (y < 41 || y > 57) return false;
    if (x >= 16 && x <= 43) return true;
    for (const [cx, rx] of [
      [12.5, 7.5],
      [47, 6.5],
    ] as const) {
      const [dx, dy] = [(x + 0.5 - cx) / rx, (y + 0.5 - 47) / 5.8];
      if (dx * dx + dy * dy <= 1) return true;
    }
    return false;
  };
  // The coat: black, lit along its top and near edges (so it reads at night), a sheen on the shoulders.
  for (let y = 41; y <= 57; y++)
    for (let x = 5; x <= 56; x++) {
      if (!inSuit(x, y)) continue;
      const lit = !inSuit(x - 1, y) || !inSuit(x, y - 1);
      const shade = !inSuit(x + 1, y) || x > 41;
      const sheen = Math.hypot(x + 0.5 - 10, y + 0.5 - 44) < 3.2 || Math.hypot(x + 0.5 - 45, y + 0.5 - 44) < 2.6;
      at(x, y, lit ? '3' : shade ? 'k' : sheen ? '2' : '1');
    }
  // The front: open from the collar down, the lapels' satin either side, the shirt above the waistcoat.
  for (let y = 41; y <= 57; y++) {
    const half = Math.max(1.5, 7 - (y - 41) * 0.35);
    for (let x = 22; x <= 38; x++) {
      const d = Math.abs(x + 0.5 - 30);
      if (d > half + 1.6) continue;
      if (d > half) {
        at(x, y, x < 30 ? '3' : '2');
        continue;
      }
      if (y < 47) at(x, y, x > 32 ? '5' : 'w');
      else at(x, y, x === 30 ? 'B' : d < 1.2 ? 'c' : x > 31 ? 'B' : 'b');
    }
  }
  // The waistcoat's points under the coat's cutaway, its gold buttons.
  for (let x = 25; x <= 35; x++) at(x, 56, Math.abs(x - 30) < 4 ? 'B' : 'b');
  for (const y of [49, 52, 55]) {
    at(30, y, 'y');
    at(30, y + 1, 'Y');
  }
  // The wing collar and the bow tie.
  c.stamp(
    `
    .ww...ww.
    w5.....5w
  `,
    26 - x0,
    41 - y0,
  );
  c.stamp(
    `
    k1...1k
    k12k21k
    k1...1k
  `,
    27 - x0,
    42 - y0,
  );
  // A white pocket square on the far breast.
  c.stamp(
    `
    w.w
    w5w
    555
  `,
    37 - x0,
    46 - y0,
  );
  // The near arm's white glove at its cuff.
  for (let y = 49; y <= 55; y++)
    for (let x = 6; x <= 17; x++) {
      const d = Math.hypot(x + 0.5 - 11.5, (y + 0.5 - 52.5) * 1.2);
      if (d < 3.8) at(x, y, d > 2.9 ? (x < 11 ? '5' : '4') : y < 52 ? 'w' : '5');
      else if (y === 49 && x >= 8 && x <= 15) at(x, y, 'w');
    }
  return [c, x0, y0];
}

/** The tailcoat lying (right of the legs, under the head): the coat's hem (left), the waistcoat, the collar by the head. */
const DEAD_COAT = grid(`
  ....1122
  ...11225
  k1122225
  k1111225
  k1111125
  k12bbb3w
  k1bccbkw
  k1bYbb1w
  k1bcbb2w
  k1bYbb1w
  k1bcbbkw
  k12bbb3w
  k1121125
  k1111225
  k1112225
  k1111225
  k1111225
  ....kkkk
`);

/** The team's trousers, black patent shoes. */
const TAILCOAT = (): Outfit => ({ torso: tailcoat(), dead: [DEAD_COAT, 24, 51], legs: { n: '1', N: 'k', m: '3' } });

// --- Abracadabra's lacquer on every other gun ------------------------------------------------------------------------

/** The stage finish (the default gun is its own, `pollinator_magician`): black lacquer for steel, gold for brass. */
const LACQUER = { '3': '1', '4': '2', '5': '3' } as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const SILVER = 0xc0cbdc;
const GREY = 0x8b9bb4;
const PINK = 0xf6757a;
const DARK = 0x181425;
const YELLOW = 0xfee761;
const GOLD = 0xfeae34;

/** The rabbit's ears (7 rows, 17 wide): up, the near one flopped over, the far one. */
const EARS = {
  up: [
    '....ww.....ww....',
    '...wppw...wppw...',
    '...wppw...wppw...',
    '...wppw...wppw...',
    '...wppw...wppw...',
    '....wpw..wpw.....',
    '....wwwwwwww.....',
  ],
  near: [
    '...........ww....',
    '..........wppw...',
    'ww........wppw...',
    'wppww.....wppw...',
    '.wpppww...wppw...',
    '...wwwww.wpw.....',
    '....wwwwwwww.....',
  ],
  far: [
    '....ww...........',
    '...wppw........ww',
    '...wppw.....wwppw',
    '...wppw...wwppw..',
    '...wppw..wppww...',
    '....wpw..www.....',
    '....wwwwwwww.....',
  ],
} as const;

/**
 * The rabbit (17 × 17), standing behind the hat's crown: `rise` rows of it from its chin up shown over the hat's top (the
 * rest hidden behind the crown, so it comes up out of it), its ears `ears`, its eyes shut with `blink`.
 */
function rabbit(rise: number, ears: keyof typeof EARS = 'up', blink = false): string {
  const art = [
    ...EARS[ears],
    '...wwwwwwwwwg....',
    '..wwwwwwwwwwwg...',
    blink ? '.wwwwwwwwwwwwwg..' : '.wwwkkwwwwkkwwg..',
    blink ? '.wwwkkwwwwkkwwg..' : '.wwwkpwwwwkpwwg..',
    '.wwwwwwppwwwwwg..',
    '.wwwwwkwwkwwwwg..',
    '.swwwwwkkwwwwwg..',
    '..sswwwwwwwwsg...',
    '....sssssssss....',
    '.................',
  ];
  return art.map((row, i) => (i < art.length - rise ? '.'.repeat(row.length) : row)).join('\n');
}

/** Nothing: the rabbit down in the hat. */
const DOWN = rabbit(0);
/** A pop: up in steps, a look round, the ears wiggling, a blink, back down; then a long wait. */
const POP = [
  ...Array<string>(28).fill(DOWN),
  rabbit(4),
  rabbit(8),
  rabbit(13),
  rabbit(17),
  rabbit(17),
  rabbit(17, 'near'),
  rabbit(17),
  rabbit(17, 'far'),
  rabbit(17),
  rabbit(17, 'up', true),
  rabbit(17),
  rabbit(17, 'near'),
  rabbit(13),
  rabbit(8),
  rabbit(3),
];
const bunny: Glyph = {
  kind: 'glyph',
  // Its chin just over the hat's top, its last row behind the crown.
  at: { on: 'head', x: 21, y: -16 },
  frames: POP,
  tones: { w: WHITE, s: SILVER, g: GREY, p: PINK, k: DARK },
  hold: [0.085, 0.085],
  order: 'cycle',
  layer: 'back',
};

const FLAIR: SetFlair = {
  head: [
    bunny,
    // A sparkle on the hat's gloss.
    {
      kind: 'twinkle',
      at: { on: 'head', x: 21, y: 8 },
      box: [6, 14],
      n: 1,
      period: [0.7, 1.2],
      tones: [WHITE, YELLOW],
      add: true,
      layer: 'front',
    },
  ],
  outfit: [
    // The cape: black, lined in the team's colour, from the shoulders, swaying.
    {
      kind: 'chain',
      at: { on: 'torso', x: 12, y: 43 },
      n: 8,
      seg: 2.2,
      dir: Math.PI * 0.6,
      stiff: 4,
      g: 300,
      drag: 0.05,
      sway: [0.08, 0.5, 0],
      wind: 200,
      w: [10, 14, 17],
      shape: 'bars',
      tones: [0x3a4466, 0x262b44, 0x262b44, 0x262b44, 0x181425],
      rim: DARK,
      lit: 'team',
      layer: 'back',
    },
    // The coat's two tails under it, swinging.
    {
      kind: 'chain',
      at: { on: 'torso', x: 16, y: 55 },
      n: 5,
      seg: 2,
      dir: Math.PI * 0.62,
      stiff: 6,
      g: 260,
      drag: 0.08,
      w: [7, 6, 6],
      shape: 'bars',
      tones: [0x262b44, 0x262b44, 0x181425],
      rim: DARK,
      lit: 0x5a6988,
      tatter: 2,
      layer: 'back',
    },
    // Sparkles twinkling off the white glove.
    {
      kind: 'twinkle',
      at: { on: 'torso', x: 11, y: 52 },
      box: [14, 12],
      round: true,
      n: 2,
      period: [0.5, 0.9],
      tones: [WHITE, YELLOW, GOLD],
      drift: [0, -6],
      add: true,
      layer: 'front',
    },
  ],
  gun: [
    // Sparkles fizzing off the wand's white tip, a glow round it.
    {
      kind: 'pulse',
      at: { on: 'gun', x: 61, y: 20 },
      r: [3, 5],
      alpha: [0.2, 0.4],
      period: 0.8,
      tone: WHITE,
      layer: 'gun',
    },
    {
      kind: 'emit',
      at: { on: 'gun', x: 62, y: 20 },
      box: [3, 3],
      rate: 9,
      v: [-6, 10, -14, 6],
      g: -10,
      drag: 0.4,
      life: [0.25, 0.5],
      size: [2, 1],
      tones: [WHITE, YELLOW, GOLD],
      alpha: [1, 0.2],
      add: true,
      layer: 'gun',
    },
  ],
};

/** The Magician set (ADR-0160 amended). */
export const MAGICIAN = drawnLater({ head: () => mask(head), outfit: TAILCOAT, gun: LACQUER, flair: FLAIR });
